import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import "core-js/modules/es6.number.constructor.js";
import { useEffect, useMemo, useRef } from 'react';
import { defer } from '@nerdwallet/utilities';
import analytics from '@nerdwallet/analytics';
import useIsInViewport from 'use-is-in-viewport';
import useABQuery from './useABQuery';
import getABBucketingId from './getABBucketingId';

var fireEEE = function fireEEE(abEvent) {
  if (typeof window === 'undefined' || !window.nwa) {
    return false;
  }

  window.nwa.track('EXPERIMENT_EXPOSURE_EVENT', _objectSpread(_objectSpread({}, abEvent), {}, {
    uuid: window.nwa.getUserProp('cookieId')
  }));
  return true;
};

// If these populate server-side, it could cause a memory leak.
// This shouldn't be a problem given this implementation because `getTestDefer` is only called within a useEffect, which only runs client-side
// Exported for testing
export var testDeferreds = {};

var getTestDefer = function getTestDefer(testName, modelExecutionId) {
  var testDefer = testDeferreds[testName];

  if (!testDefer) {
    testDefer = defer();
    testDefer.promise.then(function (experiment) {
      if (!experiment) return;
      analytics.track('ab_executed', {
        model_execution_id: modelExecutionId,
        model_name: 'ab',
        model_version: '1.0',
        // All legacy A/B experiments use version 1.0
        inputs: {
          uuid: getABBucketingId(),
          test_id: Number(experiment.experimentId)
        },
        outputs: {
          ramp: experiment.rampNum,
          variant_id: Number(experiment.assignedVariant)
        }
      });
    });
    testDeferreds[testName] = testDefer;
  }

  return testDefer;
};

var modelExecutionIds = {};

var getModelExecutionId = function getModelExecutionId(experimentId) {
  if (!experimentId) {
    return null;
  } // We need to save the model execution ids so that multiple usages of `useABTest` on the same
  // page fire with the same MEID.
  // We want to generate them outside of any `useEffect` so that they're immediately available
  // for use by other consuming components, and don't need to wait for the hook to be run a second time.
  // However, if we saved the MEIDs server-side, we'd create a memory leak, so we gate them behind
  // typeof window !== undefined


  if (typeof window === 'undefined') {
    return null;
  }

  if (modelExecutionIds[experimentId]) {
    return modelExecutionIds[experimentId];
  }

  var modelExecutionId = analytics.uuid();
  modelExecutionIds[experimentId] = modelExecutionId;
  return modelExecutionId;
};
/**
 * React hook that wraps useABQuery and encapsulates logic for firing analytics events
 */


var useABTest = function useABTest(_ref) {
  var testName = _ref.testName;

  var _useABQuery = useABQuery({
    testName: testName
  }),
      loading = _useABQuery.loading,
      variant = _useABQuery.variant,
      experiment = _useABQuery.experiment,
      isForcedVariant = _useABQuery.isForcedVariant;

  var eeeHasFired = useRef(false);

  var _useIsInViewport = useIsInViewport(),
      _useIsInViewport2 = _slicedToArray(_useIsInViewport, 2),
      isInViewport = _useIsInViewport2[0],
      ref = _useIsInViewport2[1];

  var modelExecutionId = useMemo(function () {
    return getModelExecutionId(experiment === null || experiment === void 0 ? void 0 : experiment.experimentId);
  }, [experiment === null || experiment === void 0 ? void 0 : experiment.experimentId]); // fire ab_executed event on load

  useEffect(function () {
    if (!(experiment !== null && experiment !== void 0 && experiment.experimentName)) return;
    if (experiment.isForcedVariant) return;
    getTestDefer(experiment.experimentId, modelExecutionId).resolve(experiment); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experiment === null || experiment === void 0 ? void 0 : experiment.experimentId]); // experimentId is a good proxy for all properties within experiment because if one changes, they all will
  // fire EEE event on impression

  useEffect(function () {
    if (isInViewport && !eeeHasFired.current && experiment !== null && experiment !== void 0 && experiment.usesEEE && !isForcedVariant) {
      if (fireEEE({
        test_id: Number(experiment.experimentId),
        variant_id: Number(experiment.assignedVariant),
        ramp: experiment.rampNum
      })) {
        eeeHasFired.current = true;
      }
    }
  }, [isInViewport, experiment, isForcedVariant]);
  if (loading) return {
    loading: loading
  };

  var _ref2 = variant !== null && variant !== void 0 ? variant : {},
      variantName = _ref2.variantName;

  if (!variantName) return {
    loading: loading
  };
  return {
    variantName: variantName,
    modelExecutionId: modelExecutionId,
    ref: ref,
    loading: loading
  };
};

export default useABTest;